/**
 * @lodex-pro/web-portal/hooks/useTankSetupAndRoutes — operator portal rework PR 7: the
 * company's tank records (OPERATOR_ADMIN) and its routes (OPERATOR_ADMIN, FOREMAN).
 *
 * Reads go through `useApiQuery` like the rest of the portal. Writes carry an id in the path,
 * which `useApiMutation` (a fixed path) cannot, so they use `useMutation` over `authedFetch`
 * and throw the same `ApiError` (status + catalog code), so a page can say what went wrong in
 * words. Every write refreshes both the tank-setup and the routes reads: a rename or a link
 * shows on both pages. Tank registry PR 1 (0245) adds the registry, tags and batteries; their
 * keys sit under the tank-setup key, so the same refresh covers them and the proposals.
 * Tank registry PR 2 adds the CSV / XLSX import (a multipart upload: preview, then apply).
 *
 * Types only from @lodex-pro/shared (DEC-149 §9).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { StandardTankOption } from '@lodex-pro/shared/tank-strapping';
import type {
  AddTankTagRequest,
  BatteryView,
  CompanyForemanView,
  CompanyWellView,
  ConfirmTankProposalsRequest,
  LinkTankRequest,
  OperatorTankView,
  RouteForemanRequest,
  RegistryTankView,
  RouteView,
  SaveBatteryRequest,
  SaveRegistryTankRequest,
  TankImportResponse,
  TankProposal,
  UnlinkTankRequest,
  UpdateOperatorTankRequest,
} from '@lodex-pro/shared/validation/operator-portal-routes';

const BASE = '/v1/operator-portal';

// ── Query keys ───────────────────────────────────────────────────────────────

export const TANK_SETUP_KEY: readonly string[] = ['operator-portal', 'tank-setup'];
export const TANK_PROPOSALS_KEY: readonly string[] = [...TANK_SETUP_KEY, 'proposals'];
export const OPERATOR_TANKS_KEY: readonly string[] = [...TANK_SETUP_KEY, 'tanks'];
/** Tank registry PR 1 (0245): under TANK_SETUP_KEY, so every write refreshes them too. */
export const TANK_REGISTRY_KEY: readonly string[] = [...TANK_SETUP_KEY, 'registry'];
export const BATTERIES_KEY: readonly string[] = [...TANK_SETUP_KEY, 'batteries'];
export const ROUTES_KEY: readonly string[] = ['operator-portal', 'routes'];
export const ROUTES_LIST_KEY: readonly string[] = [...ROUTES_KEY, 'list'];
export const COMPANY_WELLS_KEY: readonly string[] = [...ROUTES_KEY, 'wells'];
export const COMPANY_FOREMEN_KEY: readonly string[] = [...ROUTES_KEY, 'foremen'];

// ── Reads ────────────────────────────────────────────────────────────────────

export function useTankProposals(): ReturnType<typeof useApiQuery<{ proposals: TankProposal[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ proposals: TankProposal[] }>(opts, `${BASE}/tank-setup/proposals`, {
    queryKey: TANK_PROPOSALS_KEY,
    staleTime: 30_000,
  });
}

export function useOperatorTanks(): ReturnType<typeof useApiQuery<{ tanks: OperatorTankView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ tanks: OperatorTankView[] }>(opts, `${BASE}/tank-setup/tanks`, {
    queryKey: OPERATOR_TANKS_KEY,
    staleTime: 30_000,
  });
}

/** Tank registry PR 1 — the company's own tank records (OPERATOR_ADMIN and FOREMAN read). */
export function useRegistryTanks(): ReturnType<typeof useApiQuery<{ tanks: RegistryTankView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ tanks: RegistryTankView[] }>(opts, `${BASE}/tank-setup/registry`, {
    queryKey: TANK_REGISTRY_KEY,
    staleTime: 30_000,
  });
}

/** DEC-379 — the active standard tank sizes, for the registry form's size picker. */
export function useStandardTankOptions(): ReturnType<
  typeof useApiQuery<{ standardTanks: StandardTankOption[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ standardTanks: StandardTankOption[] }>(
    opts,
    `${BASE}/tank-setup/standard-tanks`,
    { queryKey: ['operator-portal', 'standard-tanks'], staleTime: 5 * 60_000 },
  );
}

export function useBatteries(): ReturnType<typeof useApiQuery<{ batteries: BatteryView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ batteries: BatteryView[] }>(opts, `${BASE}/tank-setup/batteries`, {
    queryKey: BATTERIES_KEY,
    staleTime: 30_000,
  });
}

export function useCompanyWells(): ReturnType<typeof useApiQuery<{ wells: CompanyWellView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ wells: CompanyWellView[] }>(opts, `${BASE}/routes/wells`, {
    queryKey: COMPANY_WELLS_KEY,
    staleTime: 60_000,
  });
}

/** Only an admin picks other people, so a foreman's page passes `enabled: false`. */
export function useCompanyForemen(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ foremen: CompanyForemanView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ foremen: CompanyForemanView[] }>(opts, `${BASE}/routes/foremen`, {
    queryKey: COMPANY_FOREMEN_KEY,
    staleTime: 60_000,
    enabled,
  });
}

export function usePortalRoutes(): ReturnType<typeof useApiQuery<{ routes: RouteView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ routes: RouteView[] }>(opts, `${BASE}/routes`, {
    queryKey: ROUTES_LIST_KEY,
    staleTime: 30_000,
  });
}

// ── Writes ───────────────────────────────────────────────────────────────────

type Method = 'POST' | 'PUT' | 'PATCH';

interface Request {
  readonly method: Method;
  readonly path: string;
  readonly body: unknown;
}

function usePortalWrite<TVars, TRes>(
  build: (vars: TVars) => Request,
): UseMutationResult<TRes, Error, TVars> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<TRes, Error, TVars>({
    mutationFn: async (vars: TVars): Promise<TRes> => {
      const req = build(vars);
      const res = await authedFetch(opts, `${BASE}${req.path}`, {
        method: req.method,
        body: JSON.stringify(req.body),
      });
      if (!res.ok) {
        throw await apiErrorFromResponse(res, req.path);
      }
      return (await res.json()) as TRes;
    },
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: TANK_SETUP_KEY });
      void qc.invalidateQueries({ queryKey: ROUTES_KEY });
    },
  });
}

export interface ConfirmResult {
  readonly tanksCreated: number;
  readonly linksCreated: number;
}

export function useConfirmProposals(): UseMutationResult<
  ConfirmResult,
  Error,
  ConfirmTankProposalsRequest
> {
  return usePortalWrite<ConfirmTankProposalsRequest, ConfirmResult>((body) => ({
    method: 'POST',
    path: '/tank-setup/confirm',
    body,
  }));
}

export function useLinkTank(): UseMutationResult<{ ok: true }, Error, LinkTankRequest> {
  return usePortalWrite<LinkTankRequest, { ok: true }>((body) => ({
    method: 'POST',
    path: '/tank-setup/link',
    body,
  }));
}

export function useUnlinkTank(): UseMutationResult<unknown, Error, UnlinkTankRequest> {
  return usePortalWrite<UnlinkTankRequest, unknown>((body) => ({
    method: 'POST',
    path: '/tank-setup/unlink',
    body,
  }));
}

export interface UpdateTankVars extends UpdateOperatorTankRequest {
  readonly id: string;
}

export function useUpdateOperatorTank(): UseMutationResult<{ ok: true }, Error, UpdateTankVars> {
  return usePortalWrite<UpdateTankVars, { ok: true }>(({ id, ...body }) => ({
    method: 'PATCH',
    path: `/tank-setup/tanks/${encodeURIComponent(id)}`,
    body,
  }));
}

// ── Tank registry PR 1 (0245) ─────────────────────────────────────────────────

/** Add (no id) or edit (id) a tank. An edit sends the full record (PUT). */
export type SaveRegistryTankVars = SaveRegistryTankRequest & { readonly id?: string };

export function useSaveRegistryTank(): UseMutationResult<
  { id: string },
  Error,
  SaveRegistryTankVars
> {
  return usePortalWrite<SaveRegistryTankVars, { id: string }>(({ id, ...body }) =>
    id === undefined
      ? { method: 'POST', path: '/tank-setup/registry', body }
      : { method: 'PUT', path: `/tank-setup/registry/${encodeURIComponent(id)}`, body },
  );
}

export type AddTankTagVars = AddTankTagRequest & { readonly tankId: string };

export function useAddTankTag(): UseMutationResult<{ id: string }, Error, AddTankTagVars> {
  return usePortalWrite<AddTankTagVars, { id: string }>(({ tankId, system, tag }) => ({
    method: 'POST',
    path: `/tank-setup/registry/${encodeURIComponent(tankId)}/tags`,
    body: { system, tag },
  }));
}

export function useRemoveTankTag(): UseMutationResult<
  { removed: boolean },
  Error,
  { tagId: string }
> {
  return usePortalWrite<{ tagId: string }, { removed: boolean }>(({ tagId }) => ({
    method: 'POST',
    path: `/tank-setup/tags/${encodeURIComponent(tagId)}/remove`,
    body: {},
  }));
}

/** Add (no id) or rename / (de)activate (id) a battery. */
export type SaveBatteryVars = SaveBatteryRequest & { readonly id?: string };

export function useSaveBattery(): UseMutationResult<{ id: string }, Error, SaveBatteryVars> {
  return usePortalWrite<SaveBatteryVars, { id: string }>(({ id, ...body }) =>
    id === undefined
      ? { method: 'POST', path: '/tank-setup/batteries', body }
      : { method: 'PATCH', path: `/tank-setup/batteries/${encodeURIComponent(id)}`, body },
  );
}

export function useSetBatteryWells(): UseMutationResult<
  { wellCount: number },
  Error,
  { id: string; wellIds: string[] }
> {
  return usePortalWrite<{ id: string; wellIds: string[] }, { wellCount: number }>(
    ({ id, wellIds }) => ({
      method: 'PUT',
      path: `/tank-setup/batteries/${encodeURIComponent(id)}/wells`,
      body: { wellIds },
    }),
  );
}

// ── Tank registry PR 2: CSV / XLSX import ─────────────────────────────────────

export interface TankImportVars {
  readonly file: File;
  /** false = preview (nothing is written); true = write. */
  readonly apply: boolean;
}

/**
 * Upload the file as one multipart `file` part. FormData: authedFetch leaves the content type to
 * the browser (it carries the boundary). The apply sends the same file again; the server re-reads
 * it and the registry, and never trusts the earlier preview. A refused file throws an ApiError
 * whose `fields` carry the server's reason (path 'file').
 */
export async function postTankImport(
  opts: ReturnType<typeof useApiOpts>,
  { file, apply }: TankImportVars,
): Promise<TankImportResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  const path = `/tank-setup/registry/import${apply ? '?apply=true' : ''}`;
  const res = await authedFetch(opts, `${BASE}${path}`, { method: 'POST', body: form });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, path);
  }
  return (await res.json()) as TankImportResponse;
}

/** Preview or apply an import. An apply refreshes the registry, batteries and proposals. */
export function useImportRegistryTanks(): UseMutationResult<
  TankImportResponse,
  Error,
  TankImportVars
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<TankImportResponse, Error, TankImportVars>({
    mutationFn: (vars) => postTankImport(opts, vars),
    onSettled: (_data, _err, vars): void => {
      if (!vars.apply) return;
      void qc.invalidateQueries({ queryKey: TANK_SETUP_KEY });
      void qc.invalidateQueries({ queryKey: ROUTES_KEY });
    },
  });
}

export function useCreateRoute(): UseMutationResult<{ id: string }, Error, { name: string }> {
  return usePortalWrite<{ name: string }, { id: string }>((body) => ({
    method: 'POST',
    path: '/routes',
    body,
  }));
}

export interface UpdateRouteVars {
  readonly id: string;
  readonly name: string;
  readonly isActive?: boolean;
}

export function useUpdateRoute(): UseMutationResult<{ id: string }, Error, UpdateRouteVars> {
  return usePortalWrite<UpdateRouteVars, { id: string }>(({ id, ...body }) => ({
    method: 'PATCH',
    path: `/routes/${encodeURIComponent(id)}`,
    body,
  }));
}

export function useSetRouteWells(): UseMutationResult<
  { ok: true },
  Error,
  { id: string; wellIds: string[] }
> {
  return usePortalWrite<{ id: string; wellIds: string[] }, { ok: true }>(({ id, wellIds }) => ({
    method: 'PUT',
    path: `/routes/${encodeURIComponent(id)}/wells`,
    body: { wellIds },
  }));
}

export function useSetRouteTanks(): UseMutationResult<
  { ok: true },
  Error,
  { id: string; tankIds: string[] }
> {
  return usePortalWrite<{ id: string; tankIds: string[] }, { ok: true }>(({ id, tankIds }) => ({
    method: 'PUT',
    path: `/routes/${encodeURIComponent(id)}/tanks`,
    body: { tankIds },
  }));
}

export interface RouteForemanVars extends RouteForemanRequest {
  readonly id: string;
}

export function useSetRouteForeman(): UseMutationResult<unknown, Error, RouteForemanVars> {
  return usePortalWrite<RouteForemanVars, unknown>(({ id, userId, on }) => ({
    method: 'POST',
    path: `/routes/${encodeURIComponent(id)}/foremen`,
    body: { userId, on },
  }));
}
